import {
  Check as CheckIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Paid as PaidIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import { formatMoney } from "../../../partner-commission/utils/formatMoney";
import {
  PARTNER_COMMISSION_STATUS_LABELS,
} from "../../../partner-commission/constants/portalEnums";
import AdminActionConfirmDialog from "../components/AdminActionConfirmDialog";
import AdminCommissionDetailDialog from "../components/AdminCommissionDetailDialog";
import MarketGrowthDataTable, {
  MarketGrowthServerPagination,
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import MarketGrowthExportButton from "../components/MarketGrowthExportButton";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import MarketGrowthStatCard from "../components/MarketGrowthStatCard";
import {
  getEnumLabel,
  toSelectOptions,
  TRIGGER_EVENT_LABELS,
} from "../constants/commissionEnums";
import { formatApiDate } from "../utils/commissionValidation";
import { openMarketGrowthExportLink } from "../utils/exportHelpers";

const PAGE_SIZE = 25;

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const PENDING_STATUSES = new Set([
  "Calculated",
  "PendingManualApproval",
  "PayoutPendingApproval",
]);

const getCommissionActions = (status) => {
  const s = String(status || "");
  const actions = [];
  if (s === "PendingManualApproval") {
    actions.push("approve-manual", "reject");
  }
  if (s === "Approved") {
    actions.push("mark-payable");
  }
  if (s === "PayoutApproved") {
    actions.push("mark-paid");
  }
  if (PENDING_STATUSES.has(s)) {
    if (!actions.includes("reject")) actions.push("reject");
  }
  return actions;
};

const ACTION_CONFIG = {
  "approve-manual": {
    title: "تأیید دستی پورسانت",
    description: "آیا از تأیید دستی این پورسانت اطمینان دارید؟",
    confirmLabel: "تأیید",
    confirmColor: "success",
  },
  reject: {
    title: "رد پورسانت",
    description: "آیا از رد این پورسانت اطمینان دارید؟",
    confirmLabel: "رد",
    confirmColor: "error",
    notesLabel: "دلیل رد (اختیاری)",
  },
  "mark-payable": {
    title: "علامت‌گذاری قابل تسویه",
    description: "این پورسانت به وضعیت قابل تسویه منتقل می‌شود.",
    confirmLabel: "تأیید",
    confirmColor: "primary",
  },
  "mark-paid": {
    title: "علامت‌گذاری پرداخت‌شده",
    description: "آیا پرداخت این پورسانت ثبت شود؟",
    confirmLabel: "ثبت پرداخت",
    confirmColor: "success",
  },
};

const PartnerCommissionsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [partners, setPartners] = useState([]);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    partnerId: "",
    status: "",
    triggerEvent: "",
    relatedUserId: "",
    relatedProjectId: "",
    fromDate: null,
    toDate: null,
  });
  const [detailId, setDetailId] = useState(null);
  const [actionState, setActionState] = useState({ open: false, row: null, action: null });
  const [saving, setSaving] = useState(false);

  const loadPartners = useCallback(async () => {
    const res = await MarketGrowthAdminApi.getPartners();
    const normalized = MarketGrowthAdminApi.normalizeList(res, "بارگذاری نمایندگان ناموفق بود.");
    if (!normalized.error) {
      setPartners(Array.isArray(normalized.data) ? normalized.data : []);
    }
  }, []);

  const buildParams = useCallback(() => {
    const params = { page, pageSize: PAGE_SIZE };
    if (filters.partnerId) params.partnerId = Number(filters.partnerId);
    if (filters.status) params.status = filters.status;
    if (filters.triggerEvent) params.triggerEvent = filters.triggerEvent;
    if (filters.relatedUserId) params.relatedUserId = Number(filters.relatedUserId);
    if (filters.relatedProjectId) params.relatedProjectId = Number(filters.relatedProjectId);
    if (filters.fromDate) params.fromDate = formatApiDate(filters.fromDate);
    if (filters.toDate) params.toDate = formatApiDate(filters.toDate);
    return params;
  }, [filters, page]);

  const buildExportParams = useCallback(() => {
    const params = {};
    if (filters.partnerId) params.partnerId = Number(filters.partnerId);
    if (filters.status) params.status = filters.status;
    if (filters.triggerEvent) params.triggerEvent = filters.triggerEvent;
    if (filters.fromDate) params.fromDate = formatApiDate(filters.fromDate);
    if (filters.toDate) params.toDate = formatApiDate(filters.toDate);
    return params;
  }, [filters]);

  const handleExport = useCallback(async () => {
    const res = await MarketGrowthAdminApi.exportCommissions(buildExportParams());
    const { error: err, data } = MarketGrowthAdminApi.normalize(
      res,
      "خروجی اکسل پورسانت‌ها ناموفق بود."
    );
    if (err) throw new Error(err);
    openMarketGrowthExportLink(data);
  }, [buildExportParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await MarketGrowthAdminApi.getCommissions(buildParams());
      const { error: err, data } = MarketGrowthAdminApi.normalize(
        res,
        "بارگذاری پورسانت‌ها ناموفق بود."
      );
      if (err) {
        setError(err);
        setItems([]);
        setTotalCount(0);
        return;
      }
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotalCount(Number(data?.totalCount) || 0);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  useEffect(() => {
    load();
  }, [load]);

  const partnerLabelMap = useMemo(
    () =>
      partners.reduce((acc, p) => {
        acc[p.id] = `کاربر ${p.userId} (${p.referralCode || p.id})`;
        return acc;
      }, {}),
    [partners]
  );

  const summary = useMemo(() => {
    const totals = { pending: 0, approved: 0, payable: 0, paid: 0 };
    items.forEach((row) => {
      const amount = Number(row.commissionAmount) || 0;
      const s = String(row.status || "");
      if (PENDING_STATUSES.has(s)) totals.pending += amount;
      else if (s === "Approved") totals.approved += amount;
      else if (s === "PayoutApproved") totals.payable += amount;
      else if (s === "Paid") totals.paid += amount;
    });
    return totals;
  }, [items]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const applyFilters = () => {
    if (page !== 1) setPage(1);
    else load();
  };

  const resolvePartnerLabel = (row) =>
    row.partnerDisplayName || partnerLabelMap[row.partnerId] || row.partnerId;

  const runAction = async (notes) => {
    if (!actionState.row || !actionState.action) return;
    setSaving(true);
    try {
      const id = actionState.row.id;
      let res;
      switch (actionState.action) {
        case "approve-manual":
          res = await MarketGrowthAdminApi.approveCommission(id, notes);
          break;
        case "reject":
          res = await MarketGrowthAdminApi.rejectCommission(id, notes);
          break;
        case "mark-payable":
          res = await MarketGrowthAdminApi.markCommissionPayable(id, notes);
          break;
        case "mark-paid":
          res = await MarketGrowthAdminApi.markCommissionPaid(id, notes);
          break;
        default:
          return;
      }
      const normalized = MarketGrowthAdminApi.normalize(res, "عملیات ناموفق بود.");
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }
      toast.success("عملیات با موفقیت انجام شد.");
      setActionState({ open: false, row: null, action: null });
      await load();
    } catch (e) {
      toast.error(e?.message || "خطا در انجام عملیات");
    } finally {
      setSaving(false);
    }
  };

  const actionConfig = actionState.action ? ACTION_CONFIG[actionState.action] : null;

  const tableRows = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        partnerLabel: resolvePartnerLabel(row),
        triggerEventLabel: getEnumLabel(TRIGGER_EVENT_LABELS, row.triggerEvent),
        statusLabel: getEnumLabel(PARTNER_COMMISSION_STATUS_LABELS, row.status),
        baseAmountLabel: formatMoney(row.baseAmount),
        commissionAmountLabel: formatMoney(row.commissionAmount),
        createdAtLabel: formatDate(row.createdAt || row.calculatedAt),
      })),
    [items, partnerLabelMap]
  );

  const headCells = useMemo(
    () => [
      { id: "partnerLabel", label: "نماینده" },
      { id: "triggerEventLabel", label: "رویداد" },
      { id: "relatedUserId", label: "کاربر مرتبط" },
      { id: "relatedProjectId", label: "پروژه مرتبط" },
      { id: "baseAmountLabel", label: "مبلغ مبنا" },
      { id: "commissionAmountLabel", label: "پورسانت" },
      { id: "statusLabel", label: "وضعیت", type: "chip" },
      { id: "createdAt", label: "تاریخ ایجاد", type: "date" },
    ],
    []
  );

  const tableActions = useMemo(
    () => [
      {
        icon: <InfoIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setDetailId(row.id),
        color: "primary",
        tooltip: "جزئیات",
      },
      {
        icon: <CheckIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setActionState({ open: true, row, action: "approve-manual" }),
        color: "success",
        tooltip: "تأیید دستی",
        hidden: (row) => !getCommissionActions(row.status).includes("approve-manual"),
      },
      {
        icon: <PaidIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setActionState({ open: true, row, action: "mark-payable" }),
        color: "primary",
        tooltip: "قابل تسویه",
        hidden: (row) => !getCommissionActions(row.status).includes("mark-payable"),
      },
      {
        icon: <PaidIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setActionState({ open: true, row, action: "mark-paid" }),
        color: "success",
        tooltip: "ثبت پرداخت",
        hidden: (row) => !getCommissionActions(row.status).includes("mark-paid"),
      },
      {
        icon: <CloseIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setActionState({ open: true, row, action: "reject" }),
        color: "error",
        tooltip: "رد",
        hidden: (row) => !getCommissionActions(row.status).includes("reject"),
      },
    ],
    []
  );

  return (
    <MarketGrowthPageShell
      title="پورسانت‌ها"
      description="مدیریت پورسانت‌های محاسبه‌شده برای نمایندگان."
      loading={loading}
      error={error}
      empty={!loading && !error && items.length === 0}
      onRetry={load}
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard
            label="در انتظار"
            value={formatMoney(summary.pending)}
            subtitle="خلاصه داده‌های بارگذاری‌شده"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard
            label="تأییدشده"
            value={formatMoney(summary.approved)}
            subtitle="خلاصه داده‌های بارگذاری‌شده"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard
            label="قابل تسویه"
            value={formatMoney(summary.payable)}
            subtitle="خلاصه داده‌های بارگذاری‌شده"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard
            label="پرداخت‌شده"
            value={formatMoney(summary.paid)}
            subtitle="خلاصه داده‌های بارگذاری‌شده"
          />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>نماینده</InputLabel>
                <Select
                  label="نماینده"
                  value={filters.partnerId}
                  onChange={(e) => setFilters((f) => ({ ...f, partnerId: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {partners.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {partnerLabelMap[p.id]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت</InputLabel>
                <Select
                  label="وضعیت"
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {toSelectOptions(PARTNER_COMMISSION_STATUS_LABELS).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>رویداد</InputLabel>
                <Select
                  label="رویداد"
                  value={filters.triggerEvent}
                  onChange={(e) => setFilters((f) => ({ ...f, triggerEvent: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {toSelectOptions(TRIGGER_EVENT_LABELS).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth
                size="small"
                label="کاربر"
                value={filters.relatedUserId}
                onChange={(e) => setFilters((f) => ({ ...f, relatedUserId: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth
                size="small"
                label="پروژه"
                value={filters.relatedProjectId}
                onChange={(e) => setFilters((f) => ({ ...f, relatedProjectId: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <JalaliDatePicker
                label="از تاریخ"
                value={filters.fromDate}
                onChange={(value) => setFilters((f) => ({ ...f, fromDate: value }))}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <JalaliDatePicker
                label="تا تاریخ"
                value={filters.toDate}
                onChange={(value) => setFilters((f) => ({ ...f, toDate: value }))}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={applyFilters}>
                  اعمال فیلتر
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  onClick={load}
                >
                  بروزرسانی
                </Button>
                <MarketGrowthExportButton onExport={handleExport} />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <MarketGrowthDataTable
            rowKey="id"
            rows={tableRows}
            headCells={headCells}
            actions={tableActions}
            pageSize={PAGE_SIZE}
            showPaging={false}
            defaultSort={{ orderBy: "createdAt", order: "desc" }}
            tableMinWidth={1100}
            actionsMode="menu"
          />
          <MarketGrowthServerPagination
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>

      <AdminCommissionDetailDialog
        open={Boolean(detailId)}
        commissionId={detailId}
        onClose={() => setDetailId(null)}
      />

      {actionConfig ? (
        <AdminActionConfirmDialog
          open={actionState.open}
          title={actionConfig.title}
          description={actionConfig.description}
          confirmLabel={actionConfig.confirmLabel}
          confirmColor={actionConfig.confirmColor}
          notesLabel={actionConfig.notesLabel}
          saving={saving}
          onClose={() => setActionState({ open: false, row: null, action: null })}
          onConfirm={runAction}
        />
      ) : null}
    </MarketGrowthPageShell>
  );
};

export default PartnerCommissionsPage;
